import React, { FC, useEffect, useState } from "react";

import { useSelector } from "react-redux";
import { MdOutlineEdit } from "react-icons/md";
import { IoDocumentOutline } from "react-icons/io5";
import logger from "../../../utils/logger";


interface InformationProps {
  leadToBeUpdate: string;
  leadData: any; // ← ADD this prop
 
}

const Information: FC<InformationProps> = ({ leadToBeUpdate, leadData: passedLeadData }) => {
  const stateData = useSelector((state: any) => state?.rootReducers);
  const [leadData, setLeadData] = useState<Record<string, any>>({});
  const [customFields, setCustomFields] = useState<Record<string, any>>({});

 

useEffect(() => {
  logger.log('📊 Information useEffect - hasData:', !!passedLeadData);
  if (passedLeadData && Object.keys(passedLeadData).length > 0) {
    processLeadData(passedLeadData);
  }
}, [passedLeadData]);

const processLeadData = (leadData: any) => {
  try {
    logger.log('🔄 Processing lead data in Information');
    const extraDetails = leadData.extraDetails || {};

    // Get customForm to identify group fields
    const customForm = stateData?.userData?.userDetails?.userPreference?.customForm || [];

    // Process extraDetails for both grouped and regular fields
    const { groupedFields, regularFields } = processExtraDetails(extraDetails, customForm);
    setCustomFields(regularFields);
    setLeadData({ groupedFields });
  } catch (error) {
    console.error("Error processing lead data:", error);
    setCustomFields({});
    setLeadData({});
  }
};

const processExtraDetails = (details: Record<string, any>, customForm: any[]): { groupedFields: any[], regularFields: Record<string, any> } => {
  const groupedFields: any[] = [];
  const regularFields: Record<string, any> = {};

  // Build a map of field values to their types from customForm
  logger.log('📋 CustomForm received:', customForm);
  const groupFieldValues = new Set<string>();
  customForm.forEach((field: any) => {
    if (field.type === 'group') {
      groupFieldValues.add(field.value);
      logger.log(`🔍 Registered group field: ${field.value} (${field.label})`);
    }
  });
  logger.log('📦 All registered group fields:', Array.from(groupFieldValues));

  try {
    Object.entries(details).forEach(([key, value]) => {
      // Skip null/undefined keys
      if (!key) return;

      try {
        const formattedKey = key
          .replace(/_/g, " ")
          .replace(/\?$/, "")
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");

        // ============================================
        // 🆕 ENHANCED: Handle ALL group fields by checking customForm
        // ============================================
        const isGroupField = groupFieldValues.has(key);
        logger.log(`🔍 Checking key: ${key}, isGroupField: ${isGroupField}, valueType: ${Array.isArray(value) ? 'array' : typeof value}`);

        if (isGroupField && Array.isArray(value) && value.length > 0) {
          logger.log(`📦 Processing group field array: ${key}`, value);

          // Each item in the array is a separate group instance
          value.forEach((groupInstance, instanceIndex) => {
            if (typeof groupInstance === 'object' && groupInstance !== null) {
              groupedFields.push({
                _isGroup: true,
                _groupTitle: formattedKey + (value.length > 1 ? ` #${instanceIndex + 1}` : ''),
                _groupFields: groupInstance
              });
            }
          });
          return; // Skip adding to regular fields
        }

        // Handle single group object (non-array)
        if (isGroupField && typeof value === 'object' && value !== null && !Array.isArray(value)) {
          logger.log(`📦 Processing single group field object: ${key}`, value);
          groupedFields.push({
            _isGroup: true,
            _groupTitle: formattedKey,
            _groupFields: value
          });
          return; // Skip adding to regular fields
        }

        // Handle question_N grouped fields (like question_1, question_2, etc.)
        if (key.startsWith('question_') && typeof value === 'object' && value !== null && !Array.isArray(value)) {
          // This contains nested group data
          Object.entries(value).forEach(([groupName, groupData]) => {
            if (typeof groupData === 'object' && groupData !== null && !Array.isArray(groupData)) {
              groupedFields.push({
                _isGroup: true,
                _groupTitle: groupName.replace(/_/g, ' '),
                _groupFields: groupData
              });
            }
          });
        }
        // Handle field_data array specially
        else if (key === "field_data" && Array.isArray(value)) {
          value.forEach((field: any) => {
            if (field?.name && field?.values !== undefined) {
              const fieldLabel = field.name
                .replace(/_/g, " ")
                .replace(/\?$/, "")
                .split(" ")
                .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ");

              const fieldValue = Array.isArray(field.values)
                ? field.values.join(", ")
                : field.values;

              regularFields[fieldLabel] = fieldValue;
            }
          });
        }
        // Handle whatsappMessage specially
        else if (key === "whatsappMessage" && typeof value === "object" && value?.body) {
          if (value.caption) {
            regularFields["WhatsappMessage"] = value.caption;
          } else {
            regularFields["WhatsappMessage"] = value.body;
          }
        }
        // Handle other objects
        else if (typeof value === "object" && value !== null) {
          if (value.body) {
            regularFields[formattedKey] = value.body;
          } else if (value.message) {
            regularFields[formattedKey] = value.message;
          } else if (value.text) {
            regularFields[formattedKey] = value.text;
          } else {
            const technicalFields = ['sha256', 'mime_type', 'id'];
            const objectEntries = Object.entries(value).filter(([k]) => !technicalFields.includes(k));

            if (objectEntries.length > 0) {
              const formattedObject = objectEntries
                .map(([k, v]) => `${k}: ${v}`)
                .join('\n');
              regularFields[formattedKey] = formattedObject;
            }
          }
        }
        // Handle primitive values
        else if (value !== null && value !== undefined && value !== "") {
          if (Array.isArray(value) && value.length === 0) {
            regularFields[formattedKey] = "No data available";
          } else {
            regularFields[formattedKey] = value;
          }
        }
      } catch (fieldError) {
        console.warn(`Error processing field ${key}:`, fieldError);
      }
    });
  } catch (error) {
    console.error("Error processing extra details:", error);
  }

  return { groupedFields, regularFields };
};

  const formatValue = (value: any): string | React.ReactNode => {
    const MAX_LENGTH = 200;

    try {
      // Handle null/undefined
      if (value === null || value === undefined) return "-";

      // Handle arrays
      if (Array.isArray(value)) {
        if (value.length === 0) return "-";
        
        // Check if array contains objects
        if (value.some(item => typeof item === "object" && item !== null)) {
          try {
            return (
              <pre
                style={{
                  background: "#f9f9f9",
                  padding: "8px",
                  borderRadius: "6px",
                  maxHeight: "150px",
                  overflow: "auto",
                  whiteSpace: "pre-wrap",
                  wordWrap: "break-word",
                  maxWidth: "100%",
                }}
              >
                {JSON.stringify(value, null, 2)}
              </pre>
            );
          } catch (error) {
            console.error("Error stringifying array of objects:", error);
            return "-";
          }
        }

        // Regular array of primitives
        return (
          <ul style={{ paddingLeft: "20px", wordWrap: "break-word" }}>
            {value.map((item, idx) => (
              <li key={idx} style={{ marginBottom: "4px", wordWrap: "break-word" }}>
                {String(item)}
              </li>
            ))}
          </ul>
        );
      }

      // Handle objects
      if (typeof value === "object" && value !== null) {
        try {
          return (
            <pre
              style={{
                background: "#f9f9f9",
                padding: "8px",
                borderRadius: "6px",
                maxHeight: "150px",
                overflow: "auto",
                whiteSpace: "pre-wrap",
                wordWrap: "break-word",
                maxWidth: "100%",
              }}
            >
              {JSON.stringify(value, null, 2)}
            </pre>
          );
        } catch (error) {
          console.error("Error stringifying object:", error);
          return "-";
        }
      }

      // Handle boolean
      if (typeof value === "boolean") return value ? "Yes" : "No";

      // Handle numbers
      if (typeof value === "number") return value.toLocaleString();

      // Handle strings
      if (typeof value === "string") {
        if (value.length > MAX_LENGTH) {
          return <ExpandableText text={value} maxLength={MAX_LENGTH} />;
        }

        if (value.startsWith("http") || value.startsWith("www")) {
          return (
            <a
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#1a73e8", wordWrap: "break-word" }}
            >
              {value}
            </a>
          );
        }

        return value || "-";
      }

      // Fallback for any other type
      return String(value) || "-";

    } catch (error) {
      console.error("Error in formatValue:", error);
      return "-";
    }
  };

  const EmptyState: FC<{ message: string }> = ({ message }) => (
    <div style={{ textAlign: "center", padding: "20px", color: "#6b7280" }}>
      <IoDocumentOutline size={40} style={{ marginBottom: "8px", color: "#9ca3af" }} />
      <p style={{ fontSize: "14px", fontWeight: 500 }}>{message}</p>
    </div>
  );

  const ExpandableText: FC<{ text: string; maxLength: number }> = ({ text, maxLength }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    return (
      <div>
        {isExpanded ? text : `${text.slice(0, maxLength)}...`}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          style={{
            background: "none",
            border: "none",
            color: "#1a73e8",
            cursor: "pointer",
            padding: "0",
            marginLeft: "8px",
            fontSize: "14px",
          }}
        >
          {isExpanded ? "Show Less" : "See More"}
        </button>
      </div>
    );
  };

 return (
  <div className="information-container p-3">
    {/* Leadform Fields Section */}
    <div className="mb-4">
      <h4 className="h5 mb-3 pb-2 border-bottom">Leadform Fields</h4>

      {/* WhatsApp Message */}
      {passedLeadData?.extraDetails?.whatsappMessage?.body && (
        <div className="card mb-3">
          <div className="card-body">
            <h6 className="card-subtitle mb-2 text-muted">WhatsappMessage</h6>
            <div className="p-3 bg-light rounded">
              <pre className="mb-0 text-wrap" style={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontSize: "0.875rem",
                fontFamily: "inherit"
              }}>
                {passedLeadData.extraDetails.whatsappMessage.body}
              </pre>
            </div>
          </div>
        </div>
      )}

      {/* Grouped Fields from Custom Forms */}
      {leadData?.groupedFields && leadData.groupedFields.length > 0 && (
        <div className="grouped-fields-container mb-3">
          {leadData.groupedFields.map((group: any, index: number) => (
            <div key={index} className="card mb-2" style={{
              background: "#f8fafc",
              border: "1px solid #cbd5e1",
              borderRadius: "6px"
            }}>
              <div className="card-body p-2">
                {/* Compact Group Header */}
                <div className="d-flex align-items-center mb-2 pb-2" style={{
                  borderBottom: "1px solid #e2e8f0"
                }}>
                  <div style={{
                    background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                    borderRadius: "4px",
                    padding: "3px 6px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: "6px"
                  }}>
                    <span style={{ fontSize: "0.85rem" }}>📋</span>
                  </div>
                  <h6 className="mb-0 fw-semibold" style={{
                    color: "#1e293b",
                    fontSize: "0.875rem"
                  }}>
                    {group._groupTitle}
                  </h6>
                </div>

                {/* Compact Group Fields - Two Column Grid */}
                <div className="row g-2">
                  {Object.entries(group._groupFields).map(([fieldKey, fieldValue]: [string, any], idx: number) => {
                    const fieldLabel = fieldKey
                      .replace(/_/g, ' ')
                      .replace(/:/g, '')
                      .split(' ')
                      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                      .join(' ');

                    // Format the value for display
                    let displayValue = fieldValue;
                    if (fieldValue === null || fieldValue === undefined || fieldValue === "") {
                      displayValue = null;
                    } else if (typeof fieldValue === "number") {
                      displayValue = fieldValue.toLocaleString();
                    } else if (typeof fieldValue === "boolean") {
                      displayValue = fieldValue ? "Yes" : "No";
                    } else if (typeof fieldValue === "object" && fieldValue !== null && !Array.isArray(fieldValue)) {
                      // Handle location fields {address, coords}
                      if (fieldValue.address) {
                        displayValue = fieldValue.address;
                      } else {
                        // For other objects, convert to readable format
                        displayValue = JSON.stringify(fieldValue);
                      }
                    }

                    return (
                      <div key={idx} className="col-6">
                        <div className="d-flex align-items-baseline gap-2 p-2" style={{
                          background: "#ffffff",
                          border: "1px solid #e2e8f0",
                          borderRadius: "4px"
                        }}>
                          <small className="text-muted" style={{
                            fontSize: "0.7rem",
                            fontWeight: 600,
                            minWidth: "fit-content"
                          }}>
                            {fieldLabel}:
                          </small>
                          <small style={{
                            color: "#1e293b",
                            fontWeight: 500,
                            fontSize: "0.75rem",
                            wordBreak: "break-word",
                            flex: 1
                          }}>
                            {Array.isArray(displayValue) ? (
                              displayValue.length > 0 ? (
                                <div className="d-flex flex-wrap gap-1">
                                  {displayValue.map((item: any, i: number) => (
                                    <span key={i} style={{
                                      padding: "2px 6px",
                                      background: "#dbeafe",
                                      color: "#1e40af",
                                      borderRadius: "3px",
                                      fontSize: "0.7rem"
                                    }}>
                                      {item}
                                    </span>
                                  ))}
                                </div>
                              ) : "-"
                            ) : displayValue || "-"}
                          </small>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>

    {/* General Information Section */}
    <div>
      <h4 className="h5 mb-3 pb-2 border-bottom">General Information</h4>

      {/* Check if we have any basic info to display */}
      {(passedLeadData?.sequenceId || passedLeadData?.phone || passedLeadData?.email || passedLeadData?.website || passedLeadData?.address || Object.keys(customFields).length > 0) ? (
        <div className="row g-3">

          {/* Lead ID */}
          {passedLeadData?.sequencePrefix && passedLeadData?.sequenceId && (
            <div className="col-12">
              <div className="d-flex align-items-center p-3 bg-light rounded">
                <i className="fas fa-hashtag me-3 text-muted"></i>
                <div>
                  <small className="text-muted d-block">Lead ID</small>
                  <span className="fw-medium">{`${passedLeadData.sequencePrefix}-${passedLeadData.sequenceId}`}</span>
                </div>
              </div>
            </div>
          )}

          {/* Phone */}
          {passedLeadData?.phone && (
            <div className="col-12">
              <div className="d-flex align-items-center p-3 bg-light rounded">
                <i className="fas fa-phone me-3 text-muted"></i>
                <div>
                  <small className="text-muted d-block">Phone</small>
                  <span className="fw-medium">{passedLeadData.phone}</span>
                </div>
              </div>
            </div>
          )}

          {/* Email */}
          {passedLeadData?.email && (
            <div className="col-12">
              <div className="d-flex align-items-center p-3 bg-light rounded">
                <i className="fas fa-envelope me-3 text-muted"></i>
                <div>
                  <small className="text-muted d-block">Email</small>
                  <span className="fw-medium">{passedLeadData.email}</span>
                </div>
              </div>
            </div>
          )}

          {/* Website */}
          {passedLeadData?.website && (
            <div className="col-12">
              <div className="d-flex align-items-center p-3 bg-light rounded">
                <i className="fas fa-globe me-3 text-muted"></i>
                <div>
                  <small className="text-muted d-block">Website</small>
                  <span className="fw-medium">{passedLeadData.website}</span>
                </div>
              </div>
            </div>
          )}

          {/* Address */}
          {passedLeadData?.address && (
            <div className="col-12">
              <div className="d-flex align-items-center p-3 bg-light rounded">
                <i className="fas fa-map-marker-alt me-3 text-muted"></i>
                <div>
                  <small className="text-muted d-block">Address</small>
                  <span className="fw-medium">{passedLeadData.address}</span>
                </div>
              </div>
            </div>
          )}

          {/* Custom Fields */}
          {Object.keys(customFields).length > 0 && (
            <div className="col-12">
              <h6 className="mt-3 mb-2">Additional Information</h6>
             {Object.entries(customFields).map(([key, value]) => (
  <div key={key} className="p-2 border-bottom">
    <small className="text-muted fw-medium d-block mb-1">{key}:</small>
    <small className="text-dark" style={{
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      lineHeight: "1.4"
    }}>
      {String(value)}
    </small>
  </div>
))}
            </div>
          )}
        </div>
      ) : (
        /* No Data State */
        <div className="text-center py-5">
          <i className="fas fa-file-alt fa-3x text-muted mb-3 opacity-50"></i>
          <p className="text-muted mb-0">
            {!passedLeadData ? "Loading lead information..." : "No General Information Available"}
          </p>
        </div>
      )}
    </div>
  </div>
);
};

export default Information;